import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SortDirection } from "@/interfaces/query-options-dto";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import {
  getInvitationDiagnosticsGetInvitationFilterConfigQueryKey,
  getInvitationDiagnosticsGetInvitationFilterConfigQueryOptions,
  invitationDiagnosticsGetInvitationFilterConfig,
  invitationDiagnosticsGetInvitationsList,
} from "@/orval/invitation-diagnostics";
import { InvitationStatus, OrganizationRoleType, type SiteAdminInvitationDiagnosticsDTO } from "@/orval/model";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Building2Icon, CheckCircleIcon, ClockIcon, EyeIcon, UserIcon, XCircleIcon } from "lucide-react";
import { useState } from "react";
import { OrganizationDetailDialog } from "./-components/organization-detail-dialog";
import { UserDetailDialog } from "./-components/user-detail-dialog";

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return <span className="text-muted-foreground">N/A</span>;
  }

  return <span className="text-sm">{format(parseISO(value), "MMM d, yyyy h:mm a")}</span>;
};

const getStatusDisplay = (status: SiteAdminInvitationDiagnosticsDTO["status"]) => {
  switch (status) {
    case InvitationStatus.Accepted:
      return {
        label: "Accepted",
        className:
          "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-800 dark:text-emerald-100 dark:border-emerald-700",
        icon: CheckCircleIcon,
      };
    case InvitationStatus.Revoked:
      return {
        label: "Revoked",
        className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-800 dark:text-red-100 dark:border-red-700",
        icon: XCircleIcon,
      };
    case InvitationStatus.Expired:
      return {
        label: "Expired",
        className:
          "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-800 dark:text-amber-100 dark:border-amber-700",
        icon: ClockIcon,
      };
    default:
      return {
        label: "Pending",
        className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-800 dark:text-blue-100 dark:border-blue-700",
        icon: ClockIcon,
      };
  }
};

const getRoleLabel = (roleType: SiteAdminInvitationDiagnosticsDTO["targetRoleType"]) => {
  switch (roleType) {
    case OrganizationRoleType.ADMIN:
      return "Admin";
    case OrganizationRoleType.PARENT:
      return "Parent";
    case OrganizationRoleType.TEACHER:
      return "Teacher";
    default:
      return "Custom";
  }
};

type InvitationDiagnosticsDialogs = {
  openOrganizationDetail: (organizationId: number) => void;
  openUserDetail: (userId: number) => void;
};

const createColumns = ({
  openOrganizationDetail,
  openUserDetail,
}: InvitationDiagnosticsDialogs): CustomColumnDef<SiteAdminInvitationDiagnosticsDTO>[] => [
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Recipient" />,
    enableSorting: true,
    size: 180,
    cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
  },
  {
    id: "Organization.Name",
    accessorFn: (row) => row.organizationName,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Organization" />,
    enableSorting: true,
    size: 160,
    cell: ({ row }) => (
      <Button
        type="button"
        variant="link"
        className="h-auto max-w-full justify-start truncate p-0 text-left font-medium"
        onClick={() => openOrganizationDetail(row.original.organizationId)}
      >
        <Building2Icon className="mr-1 size-3 shrink-0" />
        <span className="truncate">{row.original.organizationName}</span>
      </Button>
    ),
  },
  {
    accessorKey: "targetRoleType",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Target Role" />,
    enableSorting: true,
    size: 90,
    cell: ({ row }) => <Badge variant="outline">{getRoleLabel(row.original.targetRoleType)}</Badge>,
  },
  {
    accessorKey: "status",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    enableSorting: true,
    size: 100,
    cell: ({ row }) => {
      const status = getStatusDisplay(row.original.status);
      const StatusIcon = status.icon;

      return (
        <Badge variant="outline" className={`flex w-fit items-center gap-1 ${status.className}`}>
          <StatusIcon className="size-3" />
          {status.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
    enableSorting: true,
    size: 135,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
  },
  {
    accessorKey: "expiresAtUtc",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Expires" />,
    enableSorting: true,
    size: 135,
    cell: ({ row }) => formatDateTime(row.original.expiresAtUtc),
  },
  {
    accessorKey: "acceptedAtUtc",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Accepted" />,
    enableSorting: true,
    size: 135,
    cell: ({ row }) => formatDateTime(row.original.acceptedAtUtc),
  },
  {
    accessorKey: "revokedAtUtc",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Revoked" />,
    enableSorting: true,
    size: 135,
    cell: ({ row }) => formatDateTime(row.original.revokedAtUtc),
  },
  {
    accessorKey: "createdByUserEmail",
    sortField: "CreatedByUser.Email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created By" />,
    enableSorting: true,
    size: 170,
    cell: ({ row }) => (
      <Button
        type="button"
        variant="link"
        className="h-auto max-w-full justify-start truncate p-0 text-left"
        onClick={() => openUserDetail(row.original.createdByUserId)}
      >
        <UserIcon className="mr-1 size-3 shrink-0" />
        <span className="truncate">{row.original.createdByUserEmail ?? `User #${row.original.createdByUserId}`}</span>
      </Button>
    ),
  },
  {
    accessorKey: "acceptedByUserEmail",
    sortField: "AcceptedByUser.Email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Accepted By" />,
    enableSorting: true,
    size: 170,
    cell: ({ row }) =>
      row.original.acceptedByUserId ? (
        <Button
          type="button"
          variant="link"
          className="h-auto max-w-full justify-start truncate p-0 text-left"
          onClick={() => openUserDetail(row.original.acceptedByUserId!)}
        >
          <EyeIcon className="mr-1 size-3 shrink-0" />
          <span className="truncate">
            {row.original.acceptedByUserEmail ?? `User #${row.original.acceptedByUserId}`}
          </span>
        </Button>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      ),
  },
];

export const Route = createFileRoute("/_app/site-admin/invitation-diagnostics")({
  validateSearch: dataTableFiltersSearchSchema,
  loaderDeps: ({ search }) => ({ ...search }),
  loader: ({ deps, ...loaderContext }) => {
    const queryOptions = buildDataTableQueryOptions({
      searchString: deps.searchString,
      sortField: "createdAt",
      sortDirection: SortDirection.Descending,
    });

    return ensureRouteQueriesData(loaderContext, [
      getInvitationDiagnosticsGetInvitationFilterConfigQueryOptions(),
      {
        queryKey: ["site-admin-invitation-diagnostics", queryOptions, deps.filters],
        queryFn: () => invitationDiagnosticsGetInvitationsList(deps.filters ?? [], queryOptions),
      },
    ]);
  },
  component: SiteAdminInvitationDiagnosticsPage,
});

function SiteAdminInvitationDiagnosticsPage() {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isOrganizationDialogOpen, setOrganizationDialogOpen] = useState(false);
  const [isUserDialogOpen, setUserDialogOpen] = useState(false);

  const columns = createColumns({
    openOrganizationDetail: (organizationId) => {
      setSelectedOrganizationId(organizationId);
      setOrganizationDialogOpen(true);
    },
    openUserDetail: (userId) => {
      setSelectedUserId(userId);
      setUserDialogOpen(true);
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        breadcrumbs={[{ label: "Invitation Diagnostics" }]}
        title="Invitation Diagnostics"
        subtitle="Search and inspect organization invitations across the platform"
      />

      <DataTable
        columns={columns}
        queryFn={(params, _abortSignal, filters) => invitationDiagnosticsGetInvitationsList(filters ?? [], params)}
        queryKeyFn={(params, filters) => ["site-admin-invitation-diagnostics", params, filters]}
        filtersQueryFn={(abortSignal) => invitationDiagnosticsGetInvitationFilterConfig(abortSignal)}
        filtersQueryKeyFn={() => getInvitationDiagnosticsGetInvitationFilterConfigQueryKey()}
        defaultSorting={[{ id: "createdAt", desc: true }]}
        getRowIdFn={(row) => row.id.toString()}
      />

      <OrganizationDetailDialog
        isOpen={isOrganizationDialogOpen}
        onClose={() => {
          setOrganizationDialogOpen(false);
          setSelectedOrganizationId(null);
        }}
        organizationId={selectedOrganizationId}
      />

      <UserDetailDialog
        isOpen={isUserDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
      />
    </div>
  );
}
